import { ApiGetResponse, ApiRequest } from 'app/common/types'

import { AdtributeConnection } from './adtribute'
import { AmazonConfig, AmazonConnection, AmazonEntity, InstalledAmazonConnection } from './amazon'
import { CriteoConfig, CriteoConnection, CriteoEntity, InstalledCriteoConnection } from './criteo'
import { DV360Config, DV360Connection, DV360Entity, InstalledDV360Connection } from './dv360'
import {
  FacebookConfig,
  FacebookConnection,
  FacebookEntity,
  InstalledFacebookConnection,
} from './facebook'
import {
  GoogleAdwordsConfig,
  GoogleAdwordsConnection,
  GoogleAdwordsEntity,
  InstalledGoogleAdwordsConnection,
} from './googleAdwords'
import {
  GoogleAnalyticsConfig,
  GoogleAnalyticsConnection,
  GoogleAnalyticsEntity,
  InstalledGoogleAnalyticsConnection,
} from './googleAnalytics'
import {
  GoogleBigqueryConfig,
  GoogleBigqueryConnection,
  GoogleBigqueryEntity,
  InstalledGoogleBigqueryConnection,
} from './googleBigquery'
import {
  InnkeeprConfig,
  InnkeeprConnection,
  InnkeeprkEntity,
  InstalledInnkeeprConnection,
} from './innkeepr'
import { OAuthUser } from './oauthUser'
import { ShopifyConfig, ShopifyConnection, ShopifyEntity } from './shopify'
import { InstalledTiktokConnection, TiktokConfig, TiktokConnection, TiktokEntity } from './tiktok'
import {
  InstalledWoocommerceConnection,
  WoocommerceConfig,
  WoocommerceConnection,
  WoocommerceEntity,
} from './woocommerce'

export type ConnectionCategory =
  | 'advertising'
  | 'analytics'
  | 'warehouse'
  | 'website'
  | 'crm'
  | 'shopsystem'

export type ConnectionName =
  | 'amazon'
  | 'adtribute'
  | 'criteo'
  | 'dv360'
  | 'facebook'
  | 'googleAdwords'
  | 'googleAnalytics'
  | 'googleBigquery'
  | 'innkeepr'
  | 'shopify'
  | 'tiktok'
  | 'woocommerce'

export type ConnectionStatus = 'active' | 'disabled' | 'draft'

interface Connection<N extends ConnectionName> {
  category: ConnectionCategory
  name: N
}

export interface InstalledConnectionConfig {
  authorizationUser: OAuthUser | null
  id: string
  options: {
    targetingAvailable: boolean
    urlCampaignParam?: string
    urlTrackingParam: string | null
  }
  status: ConnectionStatus
  error?: string
  targetingAvailable: boolean
}

export interface ConnectionEntityType<N extends ConnectionName, C>
  extends Connection<N>, InstalledConnectionConfig {
  config: C
  deprecationError?: string
}

interface ConnectionsGetContent {
  id?: string
}
export type ConnectionEntity =
  | AmazonEntity
  | CriteoEntity
  | DV360Entity
  | FacebookEntity
  | GoogleAdwordsEntity
  | GoogleAnalyticsEntity
  | GoogleBigqueryEntity
  | InnkeeprkEntity
  | ShopifyEntity
  | TiktokEntity
  | WoocommerceEntity

export type ConnectionsGetRequest = ApiRequest<ConnectionsGetContent>
export type ConnectionsGetResponse = ApiGetResponse<ConnectionEntity[]>

export type ValidateGetRequest<V> = ApiRequest<V>
export type ValidateGetResponse = ApiGetResponse<boolean>

export type ConnectionConfigType<V = string> = Record<string, V>
export type NoConfig = ConnectionConfigType
export type ConnectionConfig =
  | AmazonConfig
  | CriteoConfig
  | DV360Config
  | FacebookConfig
  | GoogleAdwordsConfig
  | GoogleAnalyticsConfig
  | GoogleBigqueryConfig
  | InnkeeprConfig
  | NoConfig
  | ShopifyConfig
  | TiktokConfig
  | WoocommerceConfig

export interface ConnectionAuthConfig {
  params: Record<string, string>
  url: string
}
export interface AvailableConnectionConfig<
  N extends ConnectionName,
  C extends ConnectionConfig,
> extends Connection<N> {
  authConfig?: ConnectionAuthConfig
  config: C
  readonly: boolean
  readonlyConfig: string[]
}

export type AvailableConnection =
  | AdtributeConnection
  | AmazonConnection
  | CriteoConnection
  | DV360Connection
  | FacebookConnection
  | GoogleAdwordsConnection
  | GoogleAnalyticsConnection
  | GoogleBigqueryConnection
  | InnkeeprConnection
  | ShopifyConnection
  | TiktokConnection
  | WoocommerceConnection

export type InstalledConnection =
  | InstalledAmazonConnection
  | InstalledCriteoConnection
  | InstalledDV360Connection
  | InstalledFacebookConnection
  | InstalledGoogleAdwordsConnection
  | InstalledGoogleAnalyticsConnection
  | InstalledGoogleBigqueryConnection
  | InstalledInnkeeprConnection
  | InstalledTiktokConnection
  | InstalledWoocommerceConnection

export type AvailableOrInstalledConnection = AvailableConnection | InstalledConnection

export const isInstalledConnection = (conn: unknown): conn is InstalledConnection =>
  (conn as InstalledConnection).id !== undefined

const mapConnectionConfig = (connEntity: ConnectionEntity): ConnectionConfig => {
  switch (connEntity.name) {
    case 'amazon': {
      return {
        amcInstanceId: connEntity.config.amcInstanceId ?? '',
      } satisfies AmazonConfig
    }
    case 'criteo': {
      return {
        advertisers: connEntity.config.advertisers || [],
        adSetId: connEntity.options.urlTrackingParam ?? '',
        gumCallerId: connEntity.config.gumCallerId,
      } satisfies CriteoConfig
    }
    case 'dv360': {
      return {
        advertisers: connEntity.config.advertisers,
        adGroupId: connEntity.options.urlTrackingParam ?? '',
      } satisfies DV360Config
    }
    case 'facebook': {
      return {
        accounts: connEntity.config.adAccounts || [],
        // Prefer the new single field; fall back to the legacy array form.
        pixelId: connEntity.config.pixelId ?? connEntity.config.pixelIds?.[0] ?? '',
        adId: connEntity.options.urlTrackingParam ?? '',
      } satisfies FacebookConfig
    }
    case 'googleAdwords': {
      return {
        accounts: connEntity.config.adAccounts,
        campaignId: connEntity.options.urlCampaignParam ?? '',
      } satisfies GoogleAdwordsConfig
    }
    case 'googleAnalytics': {
      return {
        propertyId: connEntity.config.trackingCredentials.id,
        measurementId: connEntity.config.trackingCredentials.measurementId,
        // secret is currently not returned in api
        apiSecret: connEntity.config.trackingCredentials.secret ?? '',
      } satisfies GoogleAnalyticsConfig
    }
    case 'googleBigquery': {
      return {
        project: connEntity.config.project,
      } satisfies GoogleBigqueryConfig
    }
    case 'innkeepr': {
      return {
        trackingUrl: connEntity.config.trackingUrl ?? '',
      } satisfies InnkeeprConfig
    }
    case 'tiktok': {
      return {
        adAccountIds: connEntity.config.advertisers.map((advertiser) => advertiser.id).join(';'),
        adId: connEntity.options.urlTrackingParam ?? 'ttm',
      } satisfies TiktokConfig
    }
    case 'shopify': {
      return {
        shops: connEntity.config.map((shop) => shop.shop),
      } satisfies ShopifyConfig
    }
    case 'woocommerce': {
      return {
        shopbaseUrl: connEntity.config.shopBaseUrl,
        apiKey: connEntity.config.consumerKey ?? '',
        apiSecret: connEntity.config.secret ?? '',
      } satisfies WoocommerceConfig
    }
  }
}

export const mapToConnection = (
  connEntity: ConnectionEntity,
  availableConn: AvailableConnection,
): InstalledConnection =>
  ({
    ...availableConn,
    ...connEntity,
    authorizationUser: 'user' in connEntity.config ? connEntity.config.user : null,
    targetingAvailable: connEntity.options.targetingAvailable,
    config: mapConnectionConfig(connEntity),
  }) as InstalledConnection

export const mapToConnections = (
  connEntities: ConnectionEntity[],
  availableConnections: AvailableConnection[],
): InstalledConnection[] =>
  connEntities
    .filter((connEntity) => !connEntity.error?.includes('This connection is deprecated'))
    .map((connEntity) =>
      mapToConnection(connEntity, availableConnections.find((ac) => ac.name === connEntity.name)!),
    )
